import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiPlus, FiGlobe, FiCheck, FiX, FiAlertCircle, FiSearch, FiToggleLeft, FiToggleRight } from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { ALL_COUNTRIES, addCountry, toggleCountryActive } from '../../services/billing';

export default function CountryManager({ countries, onRefresh }) {
  const [showAdd, setShowAdd] = useState(false);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const existingCodes = new Set((countries || []).map(c => c.code));

  const availableCountries = ALL_COUNTRIES.filter(c => {
    const matchesSearch = c.name.toLowerCase().includes(search.toLowerCase()) ||
      c.code.toLowerCase().includes(search.toLowerCase()) ||
      c.currency_code.toLowerCase().includes(search.toLowerCase());
    return matchesSearch && !existingCodes.has(c.code);
  });

  const handleAdd = async () => {
    if (!selected) return;
    setError('');
    setSaving(true);
    try {
      await addCountry(selected);
      setSuccess(`Added ${selected.flag} ${selected.name}`);
      setSelected(null);
      setShowAdd(false);
      await onRefresh();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message || 'Failed to add country');
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (country) => {
    setError('');
    try {
      await toggleCountryActive(country.code, !country.is_active);
      await onRefresh();
    } catch (err) {
      setError(err.message || 'Failed to update country');
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 15 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.2 }}
      className="rounded-2xl border border-slate-200 bg-white p-6 md:p-8 shadow-sm"
    >
      <div className="flex items-start justify-between mb-6">
        <div className="flex items-start gap-4">
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 shadow-lg shrink-0">
            <SafeIcon icon={FiGlobe} className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-slate-900">Manage Countries</h3>
            <p className="text-sm text-slate-500 mt-1">
              Add countries and their default currencies. Active countries appear in the client form dropdown.
            </p>
          </div>
        </div>
        <button
          onClick={() => setShowAdd(!showAdd)}
          className={`flex items-center gap-2 rounded-xl px-4 py-2.5 text-sm font-semibold transition ${showAdd ? 'bg-slate-100 text-slate-600' : 'bg-indigo-600 text-white hover:bg-indigo-700 shadow-sm'}`}
        >
          {showAdd ? (
            <><SafeIcon icon={FiX} className="w-4 h-4" /> Close</>
          ) : (
            <><SafeIcon icon={FiPlus} className="w-4 h-4" /> Add Country</>
          )}
        </button>
      </div>

      {/* Error / Success */}
      <AnimatePresence>
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            className="mb-4 flex items-center gap-2 rounded-xl bg-red-50 border border-red-200 p-3 text-sm text-red-700"
          >
            <SafeIcon icon={FiAlertCircle} className="w-4 h-4 shrink-0" />
            {error}
          </motion.div>
        )}
        {success && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            className="mb-4 flex items-center gap-2 rounded-xl bg-emerald-50 border border-emerald-200 p-3 text-sm text-emerald-700"
          >
            <SafeIcon icon={FiCheck} className="w-4 h-4 shrink-0" />
            {success}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Add Country Panel */}
      <AnimatePresence>
        {showAdd && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden mb-6"
          >
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
              <div className="relative mb-3">
                <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  type="text"
                  placeholder="Search countries or currencies..."
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  className="w-full rounded-lg border border-slate-200 bg-white py-2.5 pl-10 pr-4 text-sm outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
                />
              </div>
              <div className="max-h-64 overflow-y-auto space-y-1">
                {availableCountries.length === 0 ? (
                  <p className="text-sm text-slate-400 text-center py-4">No more countries to add</p>
                ) : (
                  availableCountries.map(c => (
                    <button
                      key={c.code}
                      onClick={() => setSelected(selected?.code === c.code ? null : c)}
                      className={`w-full flex items-center gap-3 rounded-lg px-3 py-2.5 text-left text-sm transition ${selected?.code === c.code ? 'bg-indigo-100 border border-indigo-300' : 'hover:bg-white border border-transparent'}`}
                    >
                      <span className="text-xl">{c.flag}</span>
                      <span className="font-semibold text-slate-900 flex-1">{c.name}</span>
                      <span className="text-xs font-mono text-slate-500 bg-slate-100 px-2 py-0.5 rounded">{c.code}</span>
                      <span className="text-xs font-semibold text-indigo-600">{c.currency_code}</span>
                      <span className="text-xs text-slate-400">{c.currency_symbol}</span>
                    </button>
                  ))
                )}
              </div>
              {selected && (
                <div className="mt-3 flex items-center justify-between rounded-lg bg-white border border-indigo-200 p-3">
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">{selected.flag}</span>
                    <div>
                      <p className="text-sm font-bold text-slate-900">{selected.name}</p>
                      <p className="text-xs text-slate-500">{selected.currency_code} ({selected.currency_symbol})</p>
                    </div>
                  </div>
                  <button
                    onClick={handleAdd}
                    disabled={saving}
                    className="flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-indigo-700 disabled:opacity-50"
                  >
                    {saving ? (
                      <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <SafeIcon icon={FiPlus} className="w-4 h-4" />
                    )}
                    Add
                  </button>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Current Countries List */}
      <div className="space-y-2">
        {(countries || []).map(c => (
          <div
            key={c.code}
            className={`flex items-center gap-4 rounded-xl border p-4 transition ${c.is_active ? 'border-slate-200 bg-white' : 'border-slate-100 bg-slate-50 opacity-60'}`}
          >
            <span className="text-2xl">{c.flag}</span>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="font-bold text-slate-900">{c.name}</span>
                <span className="text-xs font-mono text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded">{c.code}</span>
              </div>
              <p className="text-sm text-slate-500">
                Currency: <span className="font-semibold text-slate-700">{c.currency_code}</span> ({c.currency_symbol})
              </p>
            </div>
            <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide ${c.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-200 text-slate-500'}`}>
              <span className={`h-1.5 w-1.5 rounded-full ${c.is_active ? 'bg-emerald-500' : 'bg-slate-400'}`} />
              {c.is_active ? 'Active' : 'Disabled'}
            </span>
            <button
              onClick={() => handleToggle(c)}
              className={`flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-semibold transition ${c.is_active ? 'text-amber-600 hover:bg-amber-50' : 'text-emerald-600 hover:bg-emerald-50'}`}
              title={c.is_active ? 'Disable country' : 'Enable country'}
            >
              <SafeIcon icon={c.is_active ? FiToggleRight : FiToggleLeft} className="w-5 h-5" />
              <span className="hidden sm:inline">{c.is_active ? 'Disable' : 'Enable'}</span>
            </button>
          </div>
        ))}
        {(!countries || countries.length === 0) && (
          <div className="text-center py-8 text-sm text-slate-400">
            No countries configured. Click "Add Country" to get started.
          </div>
        )}
      </div>
    </motion.div>
  );
}